import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type {
  DocumentAccessAction,
  DocumentAccessLogEntry,
  Document,
  DownloadUrlResponse,
  EntryAttachment,
  UploadUrlResponse,
} from '../types/api';

export interface ListDocsFilters {
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useDocumentsList(filters: ListDocsFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    enabled: !!projectId,
    queryKey: ['documents', projectId, filters],
    queryFn: async () => {
      const res = await api.get<{ documents: Document[] }>('/documents', { ...filters });
      return res.documents;
    },
  });
}

export function useRequestUploadUrl() {
  return useMutation({
    mutationFn: async (input: {
      filename: string;
      content_type: string;
      size_bytes: number;
      title?: string | null;
      description?: string | null;
    }) => api.post<UploadUrlResponse>('/documents/upload-url', input),
  });
}

export function useFinalizeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; sha256: string; page_count?: number | null }) => {
      const { id, ...body } = input;
      return api.post<{ document: Document }>(`/documents/${id}/finalize`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useRequestDownloadUrl() {
  return useMutation({
    mutationFn: async (id: string) => api.get<DownloadUrlResponse>(`/documents/${id}/download-url`),
  });
}

export function useRequestPreviewUrl() {
  return useMutation({
    mutationFn: async (id: string) =>
      api.post<DownloadUrlResponse>(`/documents/${id}/preview-url`, {}),
  });
}

export interface AccessLogFilters {
  action?: DocumentAccessAction | undefined;
  user_id?: string | undefined;
  document_id?: string | undefined;
  since?: string | undefined;
  until?: string | undefined;
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useDocumentAccessLog(filters: AccessLogFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    enabled: !!projectId,
    queryKey: ['document-access-log', projectId, filters],
    queryFn: async () => {
      const res = await api.get<{ entries: DocumentAccessLogEntry[] }>(
        '/documents/access-log',
        { ...filters },
      );
      return res.entries;
    },
  });
}

export function useSoftDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete<{ ok: true }>(`/documents/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

/* Runsheet entry attachments */

export function useEntryAttachments(entryId: string | null | undefined) {
  return useQuery({
    enabled: !!entryId,
    queryKey: ['entry-attachments', entryId],
    queryFn: async () => {
      const res = await api.get<{ attachments: EntryAttachment[] }>(
        '/runsheet-entry-documents',
        { entry_id: entryId! },
      );
      return res.attachments;
    },
  });
}

export function useAttachDocumentToEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entry_id: string;
      document_id: string;
      page_start?: number | null;
      page_end?: number | null;
      note?: string | null;
    }) =>
      api.post<{ attachment: EntryAttachment }>('/runsheet-entry-documents', input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['entry-attachments', vars.entry_id] });
    },
  });
}

export function useDetachDocumentFromEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; entry_id: string }) =>
      api.delete<void>(`/runsheet-entry-documents/${input.id}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['entry-attachments', vars.entry_id] });
    },
  });
}
